// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl } from "react-intl";

import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import {
    ListOfLinks,
    ListOfLinksProps,
    ListOfLinksHeaderText
} from "../../../components/global-nav/list-of-links/src";
import {
    ProductDetails,
    ProductCard
} from "../../../components/global-nav/product-card/src";
import Link from "../../../components/wpl/link/src/index";
import {
    getColor,
    ProductKeyTypes
} from "../../../libraries/wpl/foundations/src/index";

// ==================================================================
// Type Props
// ==================================================================
export interface ProductsDropdownProps {
    productKey: ProductKeyTypes;
    className?: string;
}

// ==================================================================
// Styles
// ==================================================================
const viewAllProductsStyle = css`
    color: #0052cc;
    &:hover {
        .link-arrow-products {
            transition: all 0.25s ease-in-out;
            padding-left: ${token("space.050")};
        }
    }
    &:hover::after {
        .link-arrow-products {
            transition: all 0.25s ease-in-out;
            padding-left: ${token("space.0")};
        }
    }
`;

const marketplaceStyle = css`
    text-decoration: none;
    &:hover {
        .link-arrow-marketplace {
            transition: all 0.25s ease-in-out;
            padding-left: ${token("space.050")};
        }
        .marketplace {
            color: #0052cc;
        }
    }
    &:hover::after {
        .link-arrow-marketplace {
            transition: all 0.25s ease-in-out;
            padding-left: ${token("space.0")};
        }
        .marketplace {
            color: #091e42;
        }
    }
    &:visited {
        color: #091e42;
    }
`;

const productCardTopRowStyle = css`
    display: grid;
    ${media.above.md} {
        grid-template-columns: 1fr 1fr;
    }
    ${UNSAFE_media.below.md} {
        grid-template-columns: 1fr;
    }
`;

const productCardBottomRowStyle = css`
    display: grid;
    ${media.above.md} {
        grid-template-columns: 1fr 1fr;
    }
    ${UNSAFE_media.below.md} {
        grid-template-columns: 1fr;
    }
`;

// ==================================================================
// Rendering Functions
// ==================================================================
const roadmapIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 36 36"
            css={css`
                margin-left: ${token("space.025")};
            `}
        >
            <path
                css={css`
                    opacity: 0.5;
                    fill: #091e42;
                    fill-rule: evenodd;
                `}
                d="M33.32,2.74,26.2,0V32.79L31.93,35a1.92,1.92,0,0,0,2.62-1.8V4.54A1.93,1.93,0,0,0,33.32,2.74Z"
            />
            <polygon
                css={css`
                    fill: #091e42;
                    fill-rule: evenodd;
                `}
                points="18.16 36 26.2 32.79 26.2 0 18.16 3.21 18.16 36"
            />
            <polygon
                css={css`
                    opacity: 0.5;
                    fill: #091e42;
                    fill-rule: evenodd;
                `}
                points="9.8 0 9.8 32.79 18.16 36 18.16 3.21 9.8 0"
            />
            <path
                css={css`
                    fill: #091e42;
                    fill-rule: evenodd;
                `}
                d="M2.68,2.74a1.93,1.93,0,0,0-1.23,1.8V33.19A1.92,1.92,0,0,0,4.07,35l5.73-2.2V0Z"
            />
        </svg>
    );
};

const linkArrow = (color: string) => {
    return (
        <svg
            css={css`
                margin-left: ${token("space.100")};
            `}
            width="11"
            height="8"
            viewBox="0 0 11 8"
            fill={getColor(color)}
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.40796 0L6.32831 1.2021L8.07854 3.1497H0.763182C0.341906 3.1497 0 3.53038 0 3.99943C0 4.46849 0.341906 4.84917 0.763182 4.84917H8.07854L6.32831 6.7979L7.40796 8L11 3.99943L7.40796 0Z"
            />
        </svg>
    );
};

const FormatViewAllProductsLink = () => {
    const intl = useIntl();
    const linkText = intl.formatMessage({
        id: "products-dropdown.main.view-products-link",
        defaultMessage: "View all products"
    });

    return (
        <div
            css={css`
                margin-top: ${token("space.200")};
                margin-bottom: ${token("space.300")};
            `}
        >
            <a
                href={useLocalizedHref("/software")}
                css={viewAllProductsStyle}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="globalNavWacDropdownContainer"
            >
                <div
                    css={css`
                        display: inline-flex;
                        font-size: 14px;
                    `}
                >
                    <div>{linkText}</div>
                    <div className="link-arrow-products">
                        {linkArrow("b400")}
                    </div>
                </div>
            </a>
        </div>
    );
};

const FormatMarketplaceLink = () => {
    const intl = useIntl();

    const marketplaceDescription = intl.formatMessage({
        id: "products-dropdown.main.marketplace-description",
        defaultMessage:
            "Connect thousands of apps and integrations for all your Atlassian products"
    });

    return (
        <div
            css={css`
                margin-top: ${token("space.150")};
                margin-bottom: ${token("space.150")};
            `}
        >
            <a
                href="//marketplace.atlassian.com"
                css={marketplaceStyle}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="globalNavWacDropdownContainer"
            >
                <div
                    css={css`
                        display: block;
                    `}
                >
                    <div
                        className="marketplace"
                        css={css`
                            font-size: ${token("space.200")};
                        `}
                    >
                        {"Marketplace"}
                    </div>
                    <div
                        css={css`
                            display: inline-flex;
                            color: #505f79;
                            font-size: ${token("space.150")};
                        `}
                    >
                        <div>{marketplaceDescription}</div>
                        <div className="link-arrow-marketplace">
                            {linkArrow("n400")}
                        </div>
                    </div>
                </div>
            </a>
        </div>
    );
};

export const ProductsMain = ({ className }: ProductsDropdownProps) => {
    const intl = useIntl();
    const mainTitle = intl.formatMessage({
        id: "products-dropdown.main.title",
        defaultMessage: "Featured"
    });

    const mainJiraSoftwareCard: ProductDetails = {
        href: useLocalizedHref("/software/jira"),
        product: "jirasoftware",
        description: intl.formatMessage({
            id: "products-dropdown.main.jira-software",
            defaultMessage: "Project and issue tracking"
        })
    };

    const mainConfluenceCard: ProductDetails = {
        href: useLocalizedHref("/software/confluence"),
        product: "confluence",
        description: intl.formatMessage({
            id: "products-dropdown.main.confluence",
            defaultMessage: "Content collaboration"
        })
    };

    const mainJiraServiceManagementCard: ProductDetails = {
        href: useLocalizedHref("/software/jira/service-management"),
        product: "jiraservicemanagement",
        description: intl.formatMessage({
            id: "products-dropdown.main.jira-service-management",
            defaultMessage: "High-velocity ITSM"
        })
    };

    const mainTrelloCard: ProductDetails = {
        href: "//trello.com/home",
        product: "trello",
        description: intl.formatMessage({
            id: "products-dropdown.main.trello",
            defaultMessage: "Visual project management"
        })
    };

    return (
        <div
            className={className}
            css={css`
                margin-top: ${token("space.300")};
            `}
        >
            <div>
                <ListOfLinksHeaderText
                    isSideBar={false}
                    headerText={mainTitle}
                />
                <div css={productCardTopRowStyle}>
                    <ProductCard productDetails={mainJiraSoftwareCard} />
                    <ProductCard productDetails={mainConfluenceCard} />
                </div>
                <div css={productCardBottomRowStyle}>
                    <ProductCard
                        productDetails={mainJiraServiceManagementCard}
                    />
                    <ProductCard productDetails={mainTrelloCard} />
                </div>
                {FormatViewAllProductsLink()}
            </div>
            <div
                css={css`
                    border-top: 0.5px solid #c1c7d0;
                    margin-right: ${token("space.150")};
                `}
            >
                {FormatMarketplaceLink()}
            </div>
        </div>
    );
};

export const ProductsSideBar = ({
    className,
    productKey
}: ProductsDropdownProps) => {
    const intl = useIntl();
    const sideBarTitle = intl.formatMessage({
        id: "products-dropdown.sidebar.title",
        defaultMessage: "What's new"
    });

    const sideBarCompassCard: ProductDetails = {
        href: useLocalizedHref("/software/compass"),
        product: "compass",
        description: intl.formatMessage({
            id: "products-dropdown.sidebar.compass",
            defaultMessage: "Developer Experience Platform"
        })
    };

    const sideBarJiraProductDiscoveryCard: ProductDetails = {
        href: useLocalizedHref("/software/jira/product-discovery"),
        product: "jiraproductdiscovery",
        description: intl.formatMessage({
            id: "products-dropdown.sidebar.jira-product-discovery",
            defaultMessage: "Prioritization and roadmapping"
        })
    };

    const viewAllText = intl.formatMessage({
        id: "products-dropdown.sidebar.view-all",
        defaultMessage: "View all"
    });

    const listOfLinksProps: ListOfLinksProps = {
        isSideBar: true,
        headerText: intl.formatMessage({
            id: "products-dropdown.sidebar.helpful",
            defaultMessage: "You might find helpful"
        }),
        listOfLinkWithProps: [
            {
                href: useLocalizedHref("/roadmap/cloud"),
                isHaveIcon: true,
                isCustomIcon: true,
                iconName: roadmapIcon(),
                linkText: intl.formatMessage({
                    id: "products-dropdown.sidebar.roadmap",
                    defaultMessage: "Cloud Product Roadmap"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/migration"),
                isHaveIcon: true,
                iconName: "cloudcheckmark",
                linkText: intl.formatMessage({
                    id: "products-dropdown.sidebar.migration",
                    defaultMessage: "Atlassian Migration Program"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            }
        ]
    };

    return (
        <div className={className}>
            <ListOfLinksHeaderText
                isSideBar={false}
                headerText={sideBarTitle}
            />
            <div>
                <ProductCard productDetails={sideBarCompassCard} />
            </div>
            <div>
                <ProductCard productDetails={sideBarJiraProductDiscoveryCard} />
            </div>
            <div
                css={css`
                    margin-bottom: ${token("space.500")};
                    margin-top: ${token("space.500")};
                `}
            >
                <Link
                    css={css`
                        &:hover,
                        &:focus {
                            text-shadow: none;
                        }
                    `}
                    text={viewAllText}
                    href={useLocalizedHref("/point-a")}
                    type="basic"
                    customLinkColor="b400"
                    dataEventProps={{
                        "data-product-key": productKey,
                        "data-event-component": "linkButton",
                        "data-event-container": "globalNavWacDropdownContainer",
                        "data-event": "clicked"
                    }}
                />
            </div>
            <ListOfLinks {...listOfLinksProps} />
        </div>
    );
};
